import { toPriceText, type PresentationContext } from './catalogCard'

/**
 * The statuses the API can return. Thirteen of them, against the three the
 * design drew.
 */
export type ApiOrderStatus =
  | 'CANCELLED'
  | 'CONFIRMED'
  | 'CREATED'
  | 'DELIVERED'
  | 'EDITED'
  | 'ERROR'
  | 'IN_PROGRESS'
  | 'PAID'
  | 'PROCESSED'
  | 'SENT'
  | 'SHIPPED'
  | 'SUGGESTED'
  | 'WAITING_CALLBACK'

/** What a badge can say. */
export type OrderRowStatus =
  | 'Confirmado'
  | 'Enviado'
  | 'Entregado'
  | 'Cancelado'
  | 'En proceso'
  | 'Con error'

/**
 * API status to the word the shopper reads.
 *
 * Six labels rather than the design's three, because collapsing further would
 * misinform: `SHIPPED` shown as "Entregado" tells someone their order arrived
 * when it is still on a truck, and `ERROR` shown as "Cancelado" claims a
 * decision nobody made. Statuses that genuinely mean "we have it, nothing has
 * moved yet" do share a label — that distinction is real to operations, not to
 * a shopper.
 *
 * Exhaustive over `ApiOrderStatus` so a status added to the enum fails to
 * compile here rather than rendering an undefined badge.
 */
const STATUS_LABEL: Record<ApiOrderStatus, OrderRowStatus> = {
  DELIVERED: 'Entregado',
  SENT: 'Enviado',
  SHIPPED: 'Enviado',
  CONFIRMED: 'Confirmado',
  PAID: 'Confirmado',
  PROCESSED: 'Confirmado',
  CREATED: 'En proceso',
  IN_PROGRESS: 'En proceso',
  EDITED: 'En proceso',
  SUGGESTED: 'En proceso',
  WAITING_CALLBACK: 'En proceso',
  CANCELLED: 'Cancelado',
  ERROR: 'Con error',
}

/**
 * A status the enum has grown since this shipped still has to render. "En
 * proceso" is the honest default: it claims nothing about delivery, payment or
 * cancellation.
 */
export const orderStatusLabel = (status: string | null | undefined): OrderRowStatus =>
  STATUS_LABEL[status as ApiOrderStatus] ?? 'En proceso'

/**
 * The API's `Date` scalar, which serialises to an **object**, not a string:
 * `{timestamp: 1786577424053, string: "23:30:24 GMT+0000 (…)"}`. Its `string`
 * is a bare time with no date in it, so only `timestamp` is usable for a
 * "6 ago 2026" label. A plain ISO string is accepted too, so a fixture or a
 * future scalar change does not have to be special-cased here.
 */
export interface ApiDate {
  timestamp?: number | null
  string?: string | null
}

export const orderDateOf = (value: ApiDate | string | number | null | undefined): Date | null => {
  if (value === null || value === undefined) return null
  const raw = typeof value === 'object' ? value.timestamp : value
  if (raw === null || raw === undefined || raw === '') return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "6 ago 2026", or an em dash when the order carries no usable date. */
export const orderDateText = (
  value: ApiDate | string | number | null | undefined,
  locale: string,
): string => {
  const date = orderDateOf(value)
  if (!date) return '—'
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

export interface OrderApiInput {
  id: string
  status?: string | null
  total?: number | null
  /** Human-facing references, either of which beats showing a Mongo id. */
  sequence?: number | null
  externalRefNumber?: string | null
  createdAt?: ApiDate | string | number | null
  items?: readonly { quantity?: number | null }[] | null
}

export interface OrderRowModel {
  /** The order's own id — what the detail route is built from. */
  id: string
  /** What the card shows as the order's name. */
  reference: string
  status: OrderRowStatus
  dateText: string
  units: number
  totalText: string
}

/**
 * Units, not lines — three of one thing is three, the same rule the cart
 * counts by.
 */
export const orderUnitCount = (items: OrderApiInput['items']): number =>
  (items ?? []).reduce((total, item) => total + (item?.quantity ?? 0), 0)

/**
 * A human reference, preferring what a person would recognise. A raw Mongo id
 * is the last resort: it is correct and unreadable, so it is what we fall back
 * to rather than what we lead with.
 */
export const orderReference = (order: OrderApiInput): string =>
  order.externalRefNumber || (order.sequence != null ? `#${order.sequence}` : '') || order.id

export const toOrderRowModel = (
  order: OrderApiInput,
  presentation: PresentationContext,
  locale: string,
): OrderRowModel => ({
  id: order.id,
  reference: orderReference(order),
  status: orderStatusLabel(order.status),
  dateText: orderDateText(order.createdAt, locale),
  units: orderUnitCount(order.items),
  totalText: toPriceText(order.total ?? 0, presentation),
})
